import React, { useState } from 'react';
import { Card, Button, Form, Alert, Spinner, Modal, Row, Col, Badge, Table } from 'react-bootstrap';
import { Plus, Search, Trash, Pencil } from 'react-bootstrap-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import StockListView from './StockListView';

const getErrorMessage = (error) => {
  if (error.response && error.response.data && error.response.data.detail) {
    // FastAPI validation errors or custom HTTPException messages
    if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail.map(err => err.msg).join('; ');
    }
    return error.response.data.detail;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred.';
};

const WatchlistManager = ({ profileId, token, onStockAdded, onRefresh }) => {
  const [tickerSymbols, setTickerSymbols] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [watchlists, setWatchlists] = useState([]);
  const [watchlistStocks, setWatchlistStocks] = useState({});
  const [stockPrices, setStockPrices] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWatchlist, setEditingWatchlist] = useState(null);
  const [updatedWatchlistName, setUpdatedWatchlistName] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

  React.useEffect(() => {
    fetchWatchlists();
  }, []);

  const normalizeQuote = (payload) => {
    const raw = Array.isArray(payload) ? payload[0] : payload;
    if (!raw || typeof raw !== 'object') return null;

    const parsePercent = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return value;
      const cleaned = String(value).replace('%', '').replace(/[()]/g, '').trim();
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const price = Number(raw.price);
    const change = Number(raw.change);
    const changePercentage = parsePercent(raw.changePercentage ?? raw.changesPercentage);

    return {
      ...raw,
      price: Number.isFinite(price) ? price : null,
      change: Number.isFinite(change) ? change : null,
      changePercentage
    };
  };

  const fetchWatchlists = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/profiles/${profileId}/watchlists`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const watchlistsData = response.data || [];
      setWatchlists(watchlistsData);
      
      // Fetch stocks for each watchlist
      for (const watchlist of watchlistsData) {
        fetchWatchlistStocks(watchlist.id);
      }
    } catch (error) {
      console.error('Error fetching watchlists:', error);
    }
  };

  const fetchWatchlistStocks = async (watchlistId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/watchlists/${watchlistId}/stocks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stocks = (response.data || []).filter(item => item && (item.stock || item.ticker_symbol));
      setWatchlistStocks(prev => ({
        ...prev,
        [watchlistId]: stocks
      }));
      
      // Fetch prices for each stock
      for (const item of stocks) {
        const symbol = item.stock?.ticker_symbol || item.ticker_symbol;
        if (symbol) {
          fetchStockPrice(symbol);
        }
      }
    } catch (error) {
      console.error(`Error fetching stocks for watchlist ${watchlistId}:`, error);
    }
  };

  const fetchStockPrice = async (symbol) => {
    try {
      console.log(`Fetching price for ${symbol}`);
      const response = await axios.get(
        `${API_BASE}/api/stock-market/fmp/${symbol}/quote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(`Price data for ${symbol}:`, response.data);
      const quote = normalizeQuote(response.data);
      if (quote) {
        setStockPrices(prev => ({
          ...prev,
          [symbol]: quote
        }));
      }
    } catch (error) {
      console.error(`Error fetching price for ${symbol}:`, error);
    }
  };

  const removeFromWatchlist = async (watchlistId, stockId) => {
    try {
      await axios.delete(
        `${API_BASE}/api/stock-market/watchlists/${watchlistId}/stocks/${stockId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setWatchlistStocks(prev => ({
        ...prev,
        [watchlistId]: (prev[watchlistId] || []).filter(item => {
          const id = item.stock?.id || item.id;
          return id !== stockId;
        })
      }));
      
      toast.success('Stock removed from watchlist');
    } catch (error) {
      console.error('Error removing stock from watchlist:', error);
      toast.error('Failed to remove stock from watchlist');
    }
  };

  const createWatchlist = async () => {
    if (!newWatchlistName.trim()) {
      toast.error('Please enter a watchlist name');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/api/stock-market/profiles/${profileId}/watchlists?name=${encodeURIComponent(newWatchlistName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWatchlists(prev => [...prev, response.data]);
      setNewWatchlistName('');
      setShowCreateModal(false);
      toast.success(`Watchlist "${newWatchlistName}" created!`);
      // Fetch stocks for the new watchlist (will be empty initially)
      fetchWatchlistStocks(response.data.id);
    } catch (error) {
      console.error('Create watchlist error:', error.response?.data);
      toast.error('Failed to create watchlist');
    }
  };

  const deleteWatchlist = async (watchlistId) => {
    if (window.confirm('Are you sure you want to delete this watchlist?')) {
      try {
        await axios.delete(
          `${API_BASE}/api/stock-market/watchlists/${watchlistId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWatchlists(prev => prev.filter(w => w.id !== watchlistId));
        toast.success('Watchlist deleted');
      } catch (error) {
        console.error('Error deleting watchlist:', error);
        toast.error('Failed to delete watchlist');
      }
    }
  };

  const openEditModal = (watchlist) => {
    setEditingWatchlist(watchlist);
    setUpdatedWatchlistName(watchlist.name);
    setShowEditModal(true);
  };

  const updateWatchlistName = async () => {
    if (!updatedWatchlistName.trim() || !editingWatchlist) {
      toast.error('Please enter a valid name');
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE}/api/stock-market/watchlists/${editingWatchlist.id}?name=${encodeURIComponent(updatedWatchlistName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWatchlists(prev => prev.map(w => w.id === editingWatchlist.id ? response.data : w));
      setShowEditModal(false);
      setEditingWatchlist(null);
      toast.success('Watchlist name updated');
    } catch (error) {
      console.error('Error updating watchlist name:', error);
      toast.error('Failed to update watchlist name');
    }
  };

  const addStockToWatchlist = async (e, watchlistId) => {
    e.preventDefault();
    const ticker = tickerSymbols[watchlistId] || '';
    if (!ticker.trim()) {
      toast.error('Please enter a ticker symbol.');
      return;
    }

    try {
      let companyName = '';
      let sector = '';
      let exchange = '';
      try {
        const profileRes = await axios.get(
          `${API_BASE}/api/stock-market/fmp/${ticker.toUpperCase()}/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        companyName = profileRes.data?.companyName || profileRes.data?.name || '';
        sector = profileRes.data?.sector || '';
        exchange = profileRes.data?.exchangeShortName || profileRes.data?.exchange || '';
      } catch (profileError) {
        // Fallback to symbol only if profile lookup fails
      }

      const response = await axios.post(
        `${API_BASE}/api/stock-market/watchlists/${watchlistId}/stocks?ticker_symbol=${ticker.toUpperCase()}&company_name=${encodeURIComponent(companyName || ticker.toUpperCase())}&sector=${encodeURIComponent(sector)}&exchange=${encodeURIComponent(exchange)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`'${ticker.toUpperCase()}' added to watchlist!`);
      setTickerSymbols(prev => ({ ...prev, [watchlistId]: '' }));
      fetchWatchlistStocks(watchlistId);
      if (onStockAdded) {
        onStockAdded(response.data.stock);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
    }
  };

  const handleTickerSymbolChange = (watchlistId, value) => {
    setTickerSymbols(prev => ({
      ...prev,
      [watchlistId]: value
    }));
  };

  return (
    <>
      {/* Create New Watchlist Card */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">📋 Watchlist Management</h5>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onRefresh}
            >
              Refresh
            </Button>
            <Button 
              variant="outline-success" 
              size="sm" 
              onClick={() => setShowCreateModal(true)}
            >
              + New Watchlist
            </Button>
          </div>
        </Card.Header>
      </Card>

      {/* Watchlist Widgets */}
      {watchlists.map(watchlist => (
        <StockListView
          key={watchlist.id}
          watchlist={watchlist}
          stocks={watchlistStocks[watchlist.id]}
          prices={stockPrices}
          onRemoveStock={removeFromWatchlist}
          onAddStock={addStockToWatchlist}
          tickerSymbol={tickerSymbols[watchlist.id] || ''}
          onTickerSymbolChange={(e) => handleTickerSymbolChange(watchlist.id, e.target.value)}
        >
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={() => openEditModal(watchlist)}
            >
              <Pencil size={12} />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => deleteWatchlist(watchlist.id)}
            >
              <Trash size={12} />
            </Button>
          </div>
        </StockListView>
        ))}

      {watchlists.length === 0 && (
        <Card>
          <Card.Body className="text-center py-5">
            <h5 className="text-muted">No Watchlists Created</h5>
            <p className="text-muted">Create your first watchlist to start tracking stocks</p>
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
            >
              + Create First Watchlist
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Create Watchlist Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Watchlist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Watchlist Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter watchlist name"
              value={newWatchlistName}
              onChange={(e) => setNewWatchlistName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createWatchlist()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createWatchlist}>
            Create Watchlist
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Watchlist Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Watchlist Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Watchlist Name</Form.Label>
            <Form.Control
              type="text"
              value={updatedWatchlistName}
              onChange={(e) => setUpdatedWatchlistName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && updateWatchlistName()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={updateWatchlistName}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WatchlistManager;
